import { ref, set, getCurrentInstance } from 'vue-demi';
import { Route } from 'vue-router';

import { useAppLauncherStore } from './use-app-launcher-store';
import { useComponentBundles } from './use-component-bundles';
import { useInitializeData } from './use-initialize-app-launcher';
import { useOneReachConfig } from './common';
import { AppState, AppKey, APPS, APP_STATES, APP_KEYS } from '../constants';

/** Prevents triggering page refresh when we change URL using API */
const isProgrammatic = ref(false);

/**
 * Use these methods to change app state.
 * TODO: We can use Vue Router to manage internal App State while managing URLs manually?
 */
export const useAppLauncherRouting = () => {
  const DEV_MODE_QUERY_PARAM = 'dev';

  const vm = getCurrentInstance()!;
  const store = useAppLauncherStore();
  const { getComponentBundle, getBundleKey } = useComponentBundles();
  const { initializeMigrationsOptions } = useInitializeData();
  const { getORConfig } = useOneReachConfig();

  const getAppByActiveRoute = () => {
    const APP_ROUTES = [
      APP_KEYS.AGENT_UI,
      APP_KEYS.APP_SETTINGS,
      APP_KEYS.UI_CUSTOMIZER,
    ];

    const name = (vm.proxy.$router.currentRoute.name || '') as AppKey;
    return APP_ROUTES.includes(name) ? name : '';
  };

  /** Change app launcher state and in certain cases change URL as well */
  const goTo = (options?: {
    state?: AppState;
    app?: AppKey;
  }) => {
    const { state = '', app = '' } = options || {};
    if (app) {
      /* Change URL only when user is redirected to a different app */
      if (app !== getAppByActiveRoute()) {
        pushRoute(app);
      }

      store.state = APP_STATES.APP_BUNDLE;
      return;
    }

    if (state) {
      store.state = state;

      /* When user reaches "Launcher Settings" state we change URL as well */
      if (state === APP_STATES.APP_SETTINGS) {
        pushRoute(APP_KEYS.APP_LAUNCHER);
      }

      /* Navigating to any other state should hide HITL app */
      if (state !== APP_STATES.APP_BUNDLE) {
        store.component = null;
      }
    }
  };

  /** Download or get bundle from cache and go to "APP_BUNDLE" state */
  const goToAppBundle = async (args: { app: AppKey; version?: string; }) => {
    const { app, version = store.settings.appVersion } = args;
    const id = getBundleKey({ key: app, version });

    if (store.componentsByKeyVersion[id]) {
      store.component = store.componentsByKeyVersion[id];
      goTo({ app });
      return;
    }

    try {
      goTo({ state: APP_STATES.DEFAULT });
      store.isLoadingBundle = true;
      store.component = null;

      const component = await getComponentBundle({
        key: APPS[app].key,
        path: APPS[app].path,
        version,
        discoveryUrl: getORConfig().SERVICE_DISCOVERY_API_URL,
      });

      set(store.componentsByKeyVersion, id, component);
      store.component = component;
      store.isLoadingBundle = false;
      goTo({ app });
    } catch (e: any) {
      store.error = e;
      store.isLoadingBundle = false;
    }
  };

  const goToAppLauncher = async (options: {
    skipInitialization?: boolean;
  } = {}) => {
    if (!options.skipInitialization) {
      try {
        store.isLoadingLauncherSettings = true;
        await initializeMigrationsOptions();
        store.isLoadingLauncherSettings = false;
      } catch (e: any) {
        store.error = e;
        store.isLoadingLauncherSettings = false;
        return;
      }
    }

    store.component = null;
    store.isVersionLowerThenMin
      /* In some cases like after manual migration we might have incompatible UI version */
      ? goTo({ state: APP_STATES.NO_APP_VERSION })
      : goTo({ state: APP_STATES.APP_SETTINGS });
  };

  const goToEnvRoot = () => window.location.assign(getORConfig().ACTION_DESK_UI_URL);
  const goToAuthUI = () => {
    const url = `${getORConfig().AUTH_UI_URL}/profile`;
    window.open(url, '_blank');
  };


  /** Use programmatic navigation to change URL  */
  const pushRoute = (name: string) => {
    /* TODO: We discard all query params during navigation except "Dev Mode" indicator */
    const isDev = vm.proxy.$router.currentRoute.query[DEV_MODE_QUERY_PARAM];
    const query = isDev === 'true' ? { [DEV_MODE_QUERY_PARAM]: 'true' } : {};

    /* Prevent "Same Route" navigation warning */
    const path = vm.proxy.$router.resolve({ name, query }).href;
    if (path !== vm.proxy.$router.currentRoute.fullPath) {
      isProgrammatic.value = true;
      vm.proxy.$router.push({ path })
        .then(() => (isProgrammatic.value = false))
        .catch(() => (isProgrammatic.value = false));
    }
  };

  /**
   * Trigger page refresh when user uses browser "Back / Forward" buttons.
   * TODO: We can implement better re-initialization but this will do in most cases.
   */
  const onURLChange = (current: Route, previous: Route) => {
    if (!isProgrammatic.value && current.name !== previous.name) {
      window.location.reload();
    }
  };

  return {
    goTo,
    goToAppBundle,
    goToAppLauncher,
    goToEnvRoot,
    goToAuthUI,
    getAppByActiveRoute,
    onURLChange,
  };
};
